import { forwardRef, useState, type ChangeEvent, type TextareaHTMLAttributes } from "react";
import { Textarea as UITextarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string;
  error?: string;
};

/** Labeled textarea with optional inline error — accepts all Textarea/HTML textarea props.
 *  Compatible with React Hook Form's `{...register("field")}` spread.
 *  Shows a live "used/max" counter whenever `maxLength` is set. */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, id, maxLength, value, defaultValue, onChange, ...props }, ref) => {
    // Uncontrolled (RHF register, no `value`) fields track length locally; controlled ones read it from `value`.
    const isControlled = value !== undefined;
    const [uncontrolledLength, setUncontrolledLength] = useState(
      () => String(defaultValue ?? "").length
    );
    const length = isControlled ? String(value ?? "").length : uncontrolledLength;

    return (
      <div className="space-y-1.5">
        {label && <Label htmlFor={id}>{label}</Label>}
        <UITextarea
          ref={ref}
          id={id}
          maxLength={maxLength}
          value={value}
          defaultValue={defaultValue}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
            if (!isControlled) setUncontrolledLength(e.target.value.length);
            onChange?.(e);
          }}
          {...props}
        />
        {error && <p className="text-sm text-destructive">{error}</p>}
        {typeof maxLength === "number" && (
          <p
            className={`text-xs text-right ${
              length > maxLength ? "text-destructive" : "text-muted-foreground"
            }`}
          >
            {length}/{maxLength}
          </p>
        )}
      </div>
    );
  }
);
Textarea.displayName = "Textarea";
